import * as XLSX from 'xlsx'

export type SheetOnboardTemplateType =
    | 'area_sheet'
    | 'unit_sheet'
    | 'infrastructure_sheet'

const fitColumns = (rows: (string | number)[][]) =>
    rows[0].map((_, col) => ({
        wch: Math.min(
            36,
            Math.max(
                14,
                ...rows.map((row) => String(row[col] ?? '').length + 2),
            ),
        ),
    }))

const addSheet = (
    workbook: XLSX.WorkBook,
    name: string,
    rows: (string | number)[][],
) => {
    const worksheet = XLSX.utils.aoa_to_sheet(rows)
    worksheet['!cols'] = fitColumns(rows)
    XLSX.utils.book_append_sheet(workbook, worksheet, name)
}

const downloadWorkbook = (workbook: XLSX.WorkBook, filename: string) => {
    XLSX.writeFile(workbook, filename)
}

const TEMPLATES: Record<
    SheetOnboardTemplateType,
    {
        filename: string
        dataSheet: string
        dataRows: (string | number)[][]
        instructions: (string | number)[][]
    }
> = {
    area_sheet: {
        filename: 'area-types-template.xlsx',
        dataSheet: 'Area Types',
        dataRows: [
            ['Name', 'Occupiable'],
            ['Bed', 1],
            ['Bath', 0],
            ['Common Area', 0],
            ['Kitchen', 0],
        ],
        instructions: [
            ['Area types template'],
            [''],
            ['What this file does'],
            [
                'Each row creates an area type for the property. Occupiable areas (beds, baths) live inside units. Common areas (kitchens, living rooms) can also be mapped onto unit types.',
            ],
            [''],
            ['Columns'],
            [
                'Name',
                'Required. Area type name, such as Bed, Bath, or Common Area.',
            ],
            [
                'Occupiable',
                'Required. 1 or TRUE = occupiable. 0 or FALSE = common.',
            ],
            [
                'Inventory / Amount',
                'Optional extra columns. Leave blank unless inventory configs already exist.',
            ],
            [''],
            ['Tips'],
            [
                'Keep names short and consistent. Unit type sheets must use these exact names.',
            ],
            [
                'The first row is the header. Do not put a real area type in row 1.',
            ],
            [
                'Replace the sample rows with your property’s area types before uploading.',
            ],
        ],
    },
    unit_sheet: {
        filename: 'unit-types-template.xlsx',
        dataSheet: 'Unit Types',
        dataRows: [
            ['Unit Type', 'Area', 'Amount'],
            ['2BR', 'Bed', 2],
            ['', 'Bath', 1],
            ['', 'Common Area', 1],
            ['1BR', 'Bed', 1],
            ['', 'Bath', 1],
            ['', 'Common Area', 1],
        ],
        instructions: [
            ['Unit types template'],
            [''],
            ['What this file does'],
            [
                'Each row adds an area type to a unit type. If Unit Type is blank, that row continues the unit type above it.',
            ],
            [''],
            ['Columns'],
            [
                'Unit Type',
                'Name of the unit type, such as 2BR. Leave blank to continue the previous unit type.',
            ],
            [
                'Area',
                'Must match an area type name from the area sheet, such as Bed or Common Area.',
            ],
            ['Amount', 'How many of that area type belong to the unit type.'],
            [''],
            ['Example'],
            ['2BR', 'Bed', 2],
            ['', 'Bath', 1],
            ['', 'Common Area', 1],
            [''],
            ['Tips'],
            ['Upload area types first, then this sheet.'],
            ['Use the same area names as the area types sheet.'],
            ['Replace the sample rows with your unit types before uploading.'],
        ],
    },
    infrastructure_sheet: {
        filename: 'buildings-and-units-template.xlsx',
        dataSheet: 'Buildings And Units',
        dataRows: [
            ['Building', 'Floor', 'DONE', 'Unit Name', 'Unit Type'],
            ['Bldg A', '1', 'DONE', '101', '2BR'],
            ['Bldg A', '1', 'DONE', '102', '1BR'],
            ['Bldg A', '2', 'DONE', '201', '2BR'],
            ['Bldg B', '1', 'DONE', '1101', '1BR'],
        ],
        instructions: [
            ['Buildings and units template'],
            [''],
            ['What this file does'],
            [
                'Each row creates folders from left to right until it hits DONE, then creates a unit with a unit type.',
            ],
            [''],
            ['Columns'],
            [
                'Folder columns',
                'Any columns before DONE become nested folders, such as Building then Floor.',
            ],
            [
                'DONE',
                'Required keyword. Everything after it is unit data, not a folder.',
            ],
            ['Unit Name', 'The unit number or name, such as 101.'],
            [
                'Unit Type',
                'Must match a unit type from the unit types sheet, such as 2BR.',
            ],
            [''],
            ['Example'],
            ['Bldg A', '1', 'DONE', '101', '2BR'],
            ['creates folder Bldg A / 1, then unit 101 as a 2BR.'],
            [''],
            ['Tips'],
            ['Upload unit types first, then this sheet.'],
            [
                'You can add more folder columns before DONE if you have buildings, floors, and stacks.',
            ],
            [
                'Replace the sample rows with your buildings and units before uploading.',
            ],
        ],
    },
}

export const SHEET_ONBOARD_TEMPLATE_COLUMNS: Record<
    SheetOnboardTemplateType,
    string[]
> = {
    area_sheet: ['Name', 'Occupiable (1 or 0)'],
    unit_sheet: ['Unit Type', 'Area', 'Amount'],
    infrastructure_sheet: [
        'Building',
        'Floor',
        'DONE',
        'Unit Name',
        'Unit Type',
    ],
}

export const downloadSheetOnboardTemplate = (
    sheetType: SheetOnboardTemplateType,
) => {
    const template = TEMPLATES[sheetType]
    const workbook = XLSX.utils.book_new()
    addSheet(workbook, template.dataSheet, template.dataRows)
    addSheet(workbook, 'Instructions', template.instructions)
    downloadWorkbook(workbook, template.filename)
}
